import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../../OBWWizard';
import { fetchPowStatus, type PowStatusResponse } from '../../../lib/pow-api';
import { AssessmentInProgress } from './AssessmentInProgress';

// POW-B (ADR-031 / ADR-029) — in-flow assessment outcome step. After submit the
// pow-agent runs ASYNC (InvocationType=Event), so this step POLLS
// GET /portal/pow/status until the Brief reaches a decided state, then shows the
// outcome IN-FLOW: auto-approved (≥75), under review (Possible Fit / 30–74), or
// declined-with-rationale (Poor Fit / <30). Client-side polling avoids the API
// Gateway 30s timeout for a slow LLM + web-fetch assessment (brief decision: b).
//
// ADR-076 §4 severance: NO navigate() (the mount routes on completion) and NO
// auth.primaryAccountId session read (identity is the mount's). The full status
// page stays reachable via the COG menu.

// Decided/terminal statuses — stop polling and render the outcome. Anything
// else (pending_validation / pending_assessment / pending_reassessment) means
// the agent is still working, so the loop keeps polling.
const DECIDED = new Set([
  'active',
  'approved',
  'staff_review',
  'rejected_auto',
  'rejected_poor_fit',
  'rejected',
  'suspended',
  'agent_failed',
]);

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 40; // ~120s ceiling for a real LLM + web-fetch assessment

type OutcomeTone = 'approved' | 'review' | 'declined' | 'error';

interface Outcome {
  tone: OutcomeTone;
  heading: string;
  body: string;
  showRationale: boolean;
}

function outcomeFor(status: string): Outcome {
  switch (status) {
    case 'active':
    case 'approved':
      return {
        tone: 'approved',
        heading: 'You\'re approved! 🎉',
        body:
          'Welcome to the Nexanomics Partner Programme. We\'ve emailed you a confirmation — your partner access is being set up and your reseller tools will appear in the navigation cog.',
        showRationale: false,
      };
    case 'staff_review':
      return {
        tone: 'review',
        heading: 'Under review',
        body:
          'Thanks — your application is with our team for a final look. We\'ll be in touch, usually within one business day. There\'s nothing more you need to do right now.',
        showRationale: false,
      };
    case 'rejected_poor_fit':
      return {
        tone: 'declined',
        heading: 'Not a fit right now',
        body:
          'Based on the assessment, your business doesn\'t look like a fit for the partner programme as it stands. The notes below explain why — you\'re welcome to refine your details and resubmit.',
        showRationale: true,
      };
    case 'rejected_auto':
    case 'rejected':
      return {
        tone: 'declined',
        heading: 'Application not approved',
        body:
          'Your application wasn\'t approved this time. The notes below explain the assessment\'s reasoning. You can address the gaps and submit a new application later.',
        showRationale: true,
      };
    case 'suspended':
      return {
        tone: 'declined',
        heading: 'Application closed',
        body: 'This application is no longer active. Contact support if you believe this is in error.',
        showRationale: false,
      };
    case 'agent_failed':
      return {
        tone: 'error',
        heading: 'We hit a snag',
        body:
          'Something went wrong while running your assessment. Our team has been notified and will review it manually — no action is needed from you.',
        showRationale: false,
      };
    default:
      return {
        tone: 'review',
        heading: 'Application received',
        body: 'Your application is on file. Check back via the cog menu for updates.',
        showRationale: false,
      };
  }
}

export function PartnerStatusStep({ data, onNext }: StepProps) {
  const submittedStatus = (data?.status as string | undefined) ?? 'pending_validation';
  const websiteUrl = (data?.websiteUrl as string | undefined) ?? '';
  const [status, setStatus] = useState<PowStatusResponse | null>(null);
  // 'polling' while the agent runs; 'decided' once terminal; 'timeout' if the
  // assessment outruns MAX_POLLS (rare — graceful fallback, not an error).
  const [phase, setPhase] = useState<'polling' | 'decided' | 'timeout'>(
    DECIDED.has(submittedStatus) ? 'decided' : 'polling',
  );
  const pollsRef = useRef(0);
  // POW-1 — the assessment's wall-clock start drives the progress bar. Use the
  // submit instant when the step carries it; otherwise anchor to first mount so
  // the elapsed clock still reads sensibly (never future/NaN).
  const startedAtRef = useRef(
    (data?.submittedAt as string | undefined) || new Date().toISOString(),
  );

  useEffect(() => {
    // If we arrived already-decided (e.g. re-entering the wizard), don't poll.
    if (phase === 'decided') {
      void fetchPowStatus().then((s) => { if (s) setStatus(s); }).catch(() => {});
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      if (cancelled) return;
      pollsRef.current += 1;
      try {
        const s = await fetchPowStatus();
        if (cancelled) return;
        if (s) {
          setStatus(s);
          if (DECIDED.has(s.status)) {
            setPhase('decided');
            return;
          }
        }
      } catch {
        // Transient poll failure — keep trying until the cap.
      }
      if (cancelled) return;
      if (pollsRef.current >= MAX_POLLS) {
        setPhase('timeout');
        return;
      }
      timer = setTimeout(() => void tick(), POLL_INTERVAL_MS);
    };

    void tick();
    return () => { cancelled = true; clearTimeout(timer); };
    // Intentionally run once on mount — the poll loop self-schedules.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Still assessing ----
  // POW-1 — live activity via the §9-forked AssessmentInProgress panel (gradient
  // progress bar + stage label + elapsed clock), replacing the bare spinner.
  // POW-2 — Done stays DISABLED while polling: the user cannot complete the
  // journey before an outcome is shown.
  if (phase === 'polling') {
    return (
      <div className="obw-step-content">
        <h2 className="obw-title">Assessing your application…</h2>
        <p className="obw-subtitle">
          We’re running the suitability assessment now. This usually takes under a minute —
          please keep this window open.
        </p>
        <AssessmentInProgress websiteUrl={websiteUrl} startedAt={startedAtRef.current} />
        <DoneButton disabled />
      </div>
    );
  }

  // ---- Timed out while still pending (graceful fallback) ----
  // POW-2 — no decision was reached, so Done stays disabled; the user closes the
  // window (✕) and we email the outcome.
  if (phase === 'timeout') {
    return (
      <div className="obw-step-content">
        <h2 className="obw-title">Still assessing</h2>
        <p className="obw-subtitle">
          Your assessment is taking a little longer than usual. We’ll email you the outcome as
          soon as it’s ready — you can close this window. You can also check back anytime via{' '}
          <strong>Cog menu → Partner application</strong>.
        </p>
        <DoneButton disabled />
      </div>
    );
  }

  // ---- Decided ----
  const renderStatus = status?.status ?? submittedStatus;
  const outcome = outcomeFor(renderStatus);
  const rationale = (status?.scoringNote ?? '').trim();

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">{outcome.heading}</h2>
      <p className="obw-subtitle">{outcome.body}</p>

      {outcome.showRationale && rationale ? (
        <div className="obw-info-box" style={{ marginTop: '16px' }}>
          <p className="obw-form-section" style={{ marginTop: 0 }}>What the assessment found</p>
          <p style={{ margin: 0, color: 'var(--text)' }}>{rationale}</p>
        </div>
      ) : null}

      <div className="obw-info-box" style={{ marginTop: '16px' }}>
        <p style={{ margin: 0 }}>
          Your application is tracked under your customer number. You can return to this status
          page anytime via <strong>Cog menu → Partner application</strong>.
        </p>
      </div>

      {/* POW-2 — only now (decided + outcome status shown) does Done enable. */}
      <DoneButton onDone={() => onNext({})} />
    </div>
  );
}

/**
 * POW-2 — the final-step completion control. pa-status is the LAST step of the
 * partner-apply journey, and the OBW completion contract is "the step renders
 * its own forward control; onNext on the last step fires the mount's
 * onComplete" (OBWWizard.handleStepNext). So Done lives HERE, gated by the
 * step's poll phase: rendered disabled while `polling`/`timeout` (no outcome to
 * act on) and enabled only once an outcome is shown — making it structurally
 * impossible to complete the journey before the assessment decides.
 */
function DoneButton({ disabled, onDone }: { disabled?: boolean; onDone?: () => void }) {
  return (
    <div className="portal-wizard-nav" style={{ marginTop: '24px' }}>
      <button
        type="button"
        className="obw-btn-primary"
        onClick={onDone}
        disabled={disabled}
        aria-label="Done"
      >
        Done
      </button>
    </div>
  );
}
